import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@store/authStore';
import { apiClient } from '@/lib/api';
import { RiBellLine, RiUserLine, RiFilterLine, RiRefreshLine, RiFileList3Line } from '@remixicon/react';
import { Card, CardContent } from '@components/ui/Card';
import { Avatar, AvatarFallback } from '@components/ui/Avatar';
import { Badge } from '@components/ui/Badge';
import { Skeleton } from '@components/ui/Skeleton';
import Button from '@components/ui/Button';
import { BidCard, BidFilters, Shortlist } from '../components';
import { transformBidData } from '../utils';
import '@/__styles/BidsList.css';

/**
 * BidsList Page
 * Main page displaying all bids with filtering and shortlist functionality
 */
const BidsList = () => {
  const navigate = useNavigate();

  // State management
  const [bids, setBids] = useState([]);
  const [filteredBids, setFilteredBids] = useState([]);
  const [shortlistedBids, setShortlistedBids] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { getUser, userProfile } = useAuthStore();

  /**
   * Load bids from API
   */
  const loadBids = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const filters = userProfile?.userprofile_id ? { broker_id: userProfile.userprofile_id } : {};
      const data = await apiClient.getBids(0, 100, filters);

      // Transform backend data to frontend format
      const transformedBids = data.map(bid => transformBidData(bid));

      setBids(transformedBids);
    } catch (err) {
      console.error('Error loading bids:', err);
      setError(err.message || 'Failed to load bids');
    } finally {
      setLoading(false);
    }
  }, [userProfile]);

  /**
   * Load shortlisted bids
   */
  const loadShortlist = useCallback(async () => {
    try {
      const data = await apiClient.getShortlistedBids();

      // Transform backend data to frontend format
      const transformedBids = data.map(bid => transformBidData(bid));

      setShortlistedBids(transformedBids);
    } catch (err) {
      console.error('Error loading shortlist:', err);
    }
  }, []);

  /**
   * Filter bids based on active filter
   */
  const filterBids = useCallback(() => {
    if (activeFilter === 'all') {
      setFilteredBids(bids);
    } else {
      const filtered = bids.filter((bid) => bid.status === activeFilter);
      setFilteredBids(filtered);
    }
  }, [activeFilter, bids]);

  // Load bids when authenticated
  useEffect(() => {
    loadBids();
    loadShortlist();
  }, [loadBids, loadShortlist]);

  // Filter bids when activeFilter or bids change
  useEffect(() => {
    filterBids();
  }, [filterBids]);

  /**
   * Handle filter change
   */
  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };

  /**
   * Handle viewing a bid
   */
  const handleViewBid = (bid) => {
    navigate(`/bids/${bid.id}`);
  };

  /**
   * Handle notification bell click
   */
  const handleNotificationClick = () => {
    // TODO: Implement notifications
    console.log('Notifications clicked');
  };

  /**
   * Handle profile click
   */
  const handleProfileClick = () => {
    navigate('/profile-setup');
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Main Content */}
      <Card className="overflow-hidden !border-gray-100">
        <CardContent className="!p-6">
        {/* Filter Section */}
        <div className="mb-6">
          <BidFilters
            activeFilter={activeFilter}
            onFilterChange={handleFilterChange}
            totalCount={filteredBids.length}
          />
        </div>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="overflow-hidden">
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-11 w-11 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                  </div>
                  <Skeleton className="h-12 w-full" />
                  <div className="flex gap-2">
                    <Skeleton className="h-5 w-20" />
                  </div>
                  <Skeleton className="h-10 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : error ? (
          <Card className="max-w-md mx-auto">
            <CardContent className="p-8 text-center space-y-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <RiRefreshLine className="w-8 h-8 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Failed to load bids</h3>
                <p className="text-sm text-red-600">{error}</p>
              </div>
              <Button
                variant="primary"
                mode="filled"
                onClick={loadBids}
                className="w-full"
              >
                <RiRefreshLine className="w-4 h-4" />
                Try Again
              </Button>
            </CardContent>
          </Card>
        ) : filteredBids.length === 0 ? (
          <Card className="max-w-md mx-auto">
            <CardContent className="p-12 text-center space-y-4">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                <RiFileList3Line className="w-10 h-10 text-gray-400" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No bids found</h3>
                <p className="text-sm text-gray-500">
                  {activeFilter === 'all'
                    ? 'You have no bids yet. Start by creating your first bid.'
                    : `No ${activeFilter} bids at the moment.`}
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {filteredBids.map((bid) => (
              <BidCard key={bid.id} bid={bid} onViewBid={handleViewBid} />
            ))}
          </div>
        )}

        {/* Shortlist Section */}
        {!loading && (
          <Shortlist
            shortlistedBids={shortlistedBids}
            onViewBid={handleViewBid}
          />
        )}
      </CardContent>
      </Card>

    </div>
  );
};

export default BidsList;